import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

/**
 * SignatureModal - Modal for creating signatures through drawing, typing, or uploading
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Called when modal closes
 * @param {Function} props.onSave - Called when signature is saved (receives dataURL)
 * @param {string} props.widgetType - 'signature' or 'initials'
 */
function SignatureModal({
  isOpen,
  onClose,
  onSave,
  widgetType = 'signature',
  applyToAllInitials = false,
  onApplyToAllInitialsChange
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('draw');
  const [typedText, setTypedText] = useState('');
  const [penColor, setPenColor] = useState('black');
  const [isLoading, setIsLoading] = useState(false);
  const signatureCanvasRef = useRef(null);
  const typedCanvasRef = useRef(null);
  const containerRef = useRef(null);

  // Resize canvas to match container width on mount and orientation change
  useEffect(() => {
    if (!isOpen || activeTab !== 'draw') return;
    const resizeCanvas = () => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;
      const wrapper = canvas.getCanvas()?.parentElement;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      if (rect.width > 0) {
        canvas.getCanvas().width = rect.width;
        canvas.getCanvas().height = 180;
        canvas.clear();
      }
    };
    // Small delay to let modal render
    const timer = setTimeout(resizeCanvas, 150);
    window.addEventListener('resize', resizeCanvas);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [isOpen, activeTab]);

  const handleClear = () => {
    if (activeTab === 'draw' && signatureCanvasRef.current) {
      signatureCanvasRef.current.clear();
    } else if (activeTab === 'type') {
      setTypedText('');
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let dataURL = null;

      if (activeTab === 'draw') {
        if (signatureCanvasRef.current && !signatureCanvasRef.current.isEmpty()) {
          // Validate drawn signature size before saving
          const strokes = signatureCanvasRef.current.toData();
          const points = [];
          strokes.forEach((stroke) => {
            (stroke.points || stroke || []).forEach((point) => points.push(point));
          });
          if (points.length > 0) {
            const xs = points.map((p) => p.x);
            const ys = points.map((p) => p.y);
            const minX = Math.min(...xs);
            const maxX = Math.max(...xs);
            const minY = Math.min(...ys);
            const maxY = Math.max(...ys);
            const drawnArea = (maxX - minX) * (maxY - minY);
            let drawnLength = 0;
            strokes.forEach((stroke) => {
              const sp = stroke.points || stroke || [];
              for (let i = 1; i < sp.length; i += 1) {
                const dx = sp[i].x - sp[i - 1].x;
                const dy = sp[i].y - sp[i - 1].y;
                drawnLength += Math.sqrt(dx * dx + dy * dy);
              }
            });
            // Pass stroke metrics so the parent can validate against admin thresholds
            dataURL = signatureCanvasRef.current.toDataURL('image/png');
            if (dataURL) {
              await onSave(dataURL, {
                applyToAllInitials: widgetType === 'initials' && applyToAllInitials,
                drawnArea,
                drawnLength
              });
              handleClose();
            }
            return;
          }
          dataURL = signatureCanvasRef.current.toDataURL('image/png');
        }
      } else if (activeTab === 'type' && typedText.trim()) {
        // Create canvas with typed text
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 150;
        const ctx = canvas.getContext('2d');
        
        // Keep initials transparent so they don't cover document content.
        if (widgetType !== 'initials') {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        
        // Draw text
        ctx.fillStyle = penColor;
        ctx.font = '48px "Brush Script MT", cursive';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedText, canvas.width / 2, canvas.height / 2);
        
        dataURL = canvas.toDataURL('image/png');
      }

      if (dataURL) {
        await onSave(dataURL, {
          applyToAllInitials: widgetType === 'initials' && applyToAllInitials
        });
        handleClose();
      }
    } catch (error) {
      console.error('Error saving signature:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    handleClear();
    setActiveTab('draw');
    setTypedText('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('signature-modal-title', {
        defaultValue: widgetType === 'initials' ? 'Add Initials' : 'Add Signature'
      })}
    >
      <div className="space-y-4">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'draw'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('draw')}
          >
            {t('signature-draw', { defaultValue: 'Draw' })}
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'type'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('type')}
          >
            {t('signature-type', { defaultValue: 'Type' })}
          </button>
        </div>

        {/* Draw Tab */}
        {activeTab === 'draw' && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-700">
                {t('signature-color', { defaultValue: 'Color:' })}
              </span>
              {['black', 'blue', 'red'].map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`h-8 w-8 rounded-full border-2 ${
                    penColor === color ? 'border-gray-800 ring-2 ring-offset-2 ring-blue-500' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setPenColor(color)}
                  aria-label={`Select ${color} color`}
                />
              ))}
            </div>

            <div className="border-2 border-gray-300 rounded-lg bg-white overflow-hidden">
              <SignatureCanvas
                ref={signatureCanvasRef}
                canvasProps={{
                  className: 'signature-canvas',
                  style: { width: '100%', height: '180px', touchAction: 'none' }
                }}
                penColor={penColor}
              />
            </div>

            <p className="text-xs text-gray-500">
              {t('signature-draw-hint', { defaultValue: 'Draw your signature above' })}
            </p>
          </div>
        )}

        {/* Type Tab */}
        {activeTab === 'type' && (
          <div className="space-y-3">
            <input
              type="text"
              value={typedText}
              onChange={(e) => setTypedText(e.target.value)}
              placeholder={t('signature-type-placeholder', {
                defaultValue: widgetType === 'initials' ? 'Type your initials' : 'Type your name'
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={50}
            />

            {typedText && (
              <div className="border-2 border-gray-300 rounded-lg bg-white p-4 flex items-center justify-center min-h-[150px]">
                <span
                  style={{
                    fontFamily: '"Brush Script MT", cursive',
                    fontSize: '48px',
                    color: penColor
                  }}
                >
                  {typedText}
                </span>
              </div>
            )}

            <p className="text-xs text-gray-500">
              {t('signature-type-hint', { defaultValue: 'Type your signature above' })}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        {widgetType === 'initials' && (
          <label className="flex items-start gap-2 rounded-lg border border-gray-200 p-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={Boolean(applyToAllInitials)}
              onChange={(event) => onApplyToAllInitialsChange?.(event.target.checked)}
            />
            <span className="text-sm text-gray-700">
              {t('initials-apply-all-widgets', {
                defaultValue: 'Usar estas mismas iniciales en todos los campos de iniciales del documento'
              })}
            </span>
          </label>
        )}

        <div className="flex justify-between items-center pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClear}
            disabled={isLoading}
          >
            {t('clear', { defaultValue: 'Clear' })}
          </Button>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading
                ? t('saving', { defaultValue: 'Saving...' })
                : t('save', { defaultValue: 'Save' })}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default SignatureModal;
